import { paymentTimeToUtc } from './purchaseTime';
export { formatPurchaseTime } from './purchaseTime';

const DECIMAL = /^\d{1,14}(?:\.\d{1,2})?$/;
const toCents = value => {
  const [whole, fraction = ''] = String(value).split('.');
  return BigInt(whole) * 100n + BigInt(fraction.padEnd(2, '0'));
};
const fromCents = value =>
  `${value / 100n}.${String(value % 100n).padStart(2, '0')}`;

export const purchaseQuantityError = (value, item) => {
  const quantity = Number(value);
  if (
    !/^\d+$/.test(String(value)) ||
    !Number.isInteger(quantity) ||
    quantity < 1 ||
    quantity > 10000
  ) {
    return 'Quantity must be a whole number between 1 and 10,000.';
  }
  if (item && quantity > item.remaining)
    return `Only ${item.remaining} bundle ${
      item.remaining === 1 ? 'unit remains' : 'units remain'
    } for this creator.`;
  return '';
};

export const limitPurchaseDiscount = (value, form, item) => {
  // Preserve empty inputs and a trailing decimal point while editing.
  if (value === '') return '';
  if (!/^\d*(?:\.\d{0,2})?$/.test(value)) return form.discountValue;
  const next = value.startsWith('.') ? `0${value}` : value;
  let maximum;
  if (form.discountType === 'percentage') {
    maximum = 10000n;
  } else if (
    form.discountType === 'fixed' &&
    DECIMAL.test(String(item?.unitPrice)) &&
    !purchaseQuantityError(form.quantity)
  ) {
    maximum = toCents(item.unitPrice) * BigInt(form.quantity);
  }
  if (maximum !== undefined && toCents(next) > maximum)
    return form.discountType === 'percentage' ? '100' : fromCents(maximum);
  return next;
};

export const calculatePurchasePrice = (form, item) => {
  if (!item?.id || !item.purchasable)
    throw new Error('Choose an available ticket bundle.');
  const quantityError = purchaseQuantityError(form.quantity, item);
  if (quantityError) throw new Error(quantityError);
  const quantity = Number(form.quantity);
  const discountType = form.discountType === '' ? 'none' : form.discountType;
  if (!['none', 'percentage', 'fixed'].includes(discountType))
    throw new Error('Choose a discount type.');
  const discountValue = discountType === 'none' ? '0.00' : form.discountValue;
  if (!DECIMAL.test(discountValue) || !Number.isFinite(Number(discountValue))) {
    throw new Error(
      'Enter a nonnegative discount with up to two decimal places, using a dot.'
    );
  }
  if (discountType === 'percentage' && Number(discountValue) > 100) {
    throw new Error('Percentage discount cannot exceed 100%.');
  }
  if (!DECIMAL.test(String(item.unitPrice)))
    throw new Error('The bundle price is invalid. Select the item again.');
  const subtotal = toCents(item.unitPrice) * BigInt(quantity);
  if (!DECIMAL.test(fromCents(subtotal)))
    throw new Error('The purchase subtotal exceeds the supported amount.');
  // Match the backend: calculate in cents and round percentage ties half-up.
  const discountAmount =
    discountType === 'percentage'
      ? (subtotal * toCents(discountValue) + 5000n) / 10000n
      : discountType === 'fixed'
      ? toCents(discountValue)
      : 0n;
  if (discountAmount > subtotal)
    throw new Error('Fixed discount cannot exceed the subtotal.');
  return {
    quantity,
    discountType,
    discountValue,
    subtotal: fromCents(subtotal),
    discountAmount: fromCents(discountAmount),
    total: fromCents(subtotal - discountAmount),
  };
};

export const buildPurchaseInput = (form, creator, item, evidenceId) => {
  if (!creator?.id)
    throw new Error('Choose a creator from the search results.');
  const { quantity, discountType, discountValue } = calculatePurchasePrice(
    form,
    item
  );
  const reference = form.reference.trim();
  if (!reference || reference.length > 255)
    throw new Error('Enter a payment reference of 1–255 characters.');
  const paymentAt = new Date(
    paymentTimeToUtc(form.paymentAt, form.timeZone, form.paymentOffset)
  );
  if (
    !form.paymentAt ||
    !Number.isFinite(paymentAt.getTime()) ||
    paymentAt.getTime() > Date.now()
  ) {
    throw new Error(
      'Enter a valid payment date and time that is not in the future.'
    );
  }
  return {
    creatorId: creator.id,
    itemType: 'ticket_bundle',
    itemId: item.id,
    quantity,
    discountType,
    discountValue,
    reference,
    paymentAt: paymentAt.toISOString(),
    ...(evidenceId ? { evidenceId } : {}),
  };
};

export const validateEvidence = file => {
  if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type))
    throw new Error('Choose a JPEG, PNG or WebP image.');
  if (!file.size || file.size > 20 * 1024 * 1024)
    throw new Error('Evidence must be a nonempty image of up to 20 MiB.');
};

// Ticket issuance completes the purchase UI; VP distribution runs separately.
export const isPurchaseFulfilled = purchase =>
  purchase?.issuanceStatus === 'fulfilled';

// Keep one submitted intent across refreshes, scoped to this admin and API environment.
export const intentStorageKey = userId =>
  `skai1_manual_purchase:${process.env.REACT_APP_SKAI_ONE_URL}:${userId}`;

export const readPurchaseIntent = userId => {
  const value = sessionStorage.getItem(intentStorageKey(userId));
  if (!value) return null;
  const intent = JSON.parse(value);
  if (!intent?.payload?.idempotencyKey || !intent?.review) {
    throw new Error(
      'The saved purchase could not be recovered. Check the recorded transaction before starting another purchase.'
    );
  }
  return intent;
};

export const savePurchaseIntent = (userId, intent) => {
  try {
    sessionStorage.setItem(intentStorageKey(userId), JSON.stringify(intent));
  } catch (error) {
    throw new Error(
      'Your browser could not save this purchase for recovery. Enable session storage before confirming.'
    );
  }
};

export const clearPurchaseIntent = userId =>
  sessionStorage.removeItem(intentStorageKey(userId));

export const formatPurchaseMoney = value => {
  const format = new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'EUR',
  });
  if (!DECIMAL.test(String(value))) return format.format(Number(value));
  const [whole, fraction = ''] = String(value).split('.');
  return format
    .formatToParts(BigInt(whole))
    .map(part =>
      part.type === 'fraction' ? fraction.padEnd(2, '0') : part.value
    )
    .join('');
};
